import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './EmployeeTaskReport.css';

const API_URL = process.env.REACT_APP_API_URL;

const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fmtDate = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

const MARKER_LABELS = { S: 'Start', L: 'Leave', B: 'Break', I: 'Resume' };

export default function EmployeeTaskReport() {
  const { user } = useContext(AuthContext);
  const auth = () => ({ headers: { Authorization: `Bearer ${user?.token}` } });

  const today = localToday();
  const [employees, setEmployees] = useState([]);
  const [empId,     setEmpId]     = useState('');
  const [dateFrom,  setDateFrom]  = useState(today);
  const [dateTo,    setDateTo]    = useState(today);
  const [rows,      setRows]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/my-tasks/employees`, auth())
      .then(r => setEmployees(r.data))
      .catch(() => setError('Failed to load employee list.'));
  }, []); // eslint-disable-line

  const run = async () => {
    if (!empId) { setError('Please select an employee.'); return; }
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(
        `${API_URL}/my-tasks/employee-report?employee_id=${empId}&date_from=${dateFrom}&date_to=${dateTo}`,
        auth()
      );
      setRows(r.data);
    } catch {
      setError('Failed to load tasks.');
    } finally {
      setLoading(false);
    }
  };

  // Group rows by date
  const byDate = rows
    ? rows.reduce((acc, row) => {
        (acc[row.task_date] = acc[row.task_date] || []).push(row);
        return acc;
      }, {})
    : null;

  const selectedEmp = employees.find(e => String(e.id) === String(empId));

  return (
    <div className="etr-page">
      <h2 className="etr-title">Employee Task Report</h2>

      <div className="etr-filters">
        <div className="etr-field">
          <label className="etr-label">Employee</label>
          <select className="etr-select" value={empId} onChange={e => setEmpId(e.target.value)}>
            <option value="">— Select Employee —</option>
            {employees.map(e => (
              <option key={e.id} value={e.id}>{e.emp_name}</option>
            ))}
          </select>
        </div>
        <div className="etr-field">
          <label className="etr-label">From</label>
          <input type="date" className="etr-input" value={dateFrom}
            max={today} onChange={e => setDateFrom(e.target.value)} />
        </div>
        <div className="etr-field">
          <label className="etr-label">To</label>
          <input type="date" className="etr-input" value={dateTo}
            min={dateFrom} max={today} onChange={e => setDateTo(e.target.value)} />
        </div>
        <button className="etr-btn" onClick={run} disabled={loading || !empId}>
          {loading ? 'Loading…' : 'Run Report'}
        </button>
      </div>

      {error && <p className="etr-error">{error}</p>}

      {byDate !== null && (
        Object.keys(byDate).length === 0 ? (
          <p className="etr-empty">No tasks found for {selectedEmp?.emp_name} in the selected period.</p>
        ) : (
          <div className="etr-results">
            {Object.entries(byDate).map(([date, tasks]) => (
              <div key={date} className="etr-day">
                <div className="etr-day-header">{fmtDate(date)}</div>
                <table className="etr-table">
                  <thead>
                    <tr>
                      <th className="etr-col-time">Time</th>
                      <th>Task</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.map((t, i) => {
                      const isMarker = MARKER_LABELS[t.task];
                      return (
                        <tr key={i} className={isMarker ? 'etr-row-marker' : ''}>
                          <td className="etr-col-time etr-time">{t.task_time}</td>
                          <td className={isMarker ? 'etr-marker-text' : 'etr-task-text'}>
                            {isMarker ? MARKER_LABELS[t.task] : t.task}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
